export interface SEOData {
  title: string
  description: string
  keywords?: string
  ogImage?: string
  canonical?: string
  locale?: string
  alternateLocales?: { [key: string]: string }
}

export const useSEO = (data: SEOData) => {
  const { $i18n } = useNuxtApp()
  const localePath = useLocalePath()
  const route = useRoute()
  
  // Get current locale
  const currentLocale = data.locale || $i18n.locale.value
  
  // Build canonical URL
  const canonicalUrl = data.canonical || `https://shiftgo.dk${route.path}`
  
  // Build alternate URLs for hreflang
  const alternateUrls: { [key: string]: string } = {}
  const locales = ['en', 'tr', 'dk']
  
  locales.forEach(locale => {
    if (data.alternateLocales && data.alternateLocales[locale]) {
      alternateUrls[locale] = `https://shiftgo.dk${data.alternateLocales[locale]}`
    } else {
      // Generate alternate URL based on current path
      const alternatePath = localePath({ path: route.path }, locale)
      alternateUrls[locale] = `https://shiftgo.dk${alternatePath}`
    }
  })
  
  // Set comprehensive SEO meta
  useSeoMeta({
    title: data.title,
    description: data.description,
    keywords: data.keywords,
    
    // Open Graph
    ogTitle: data.title,
    ogDescription: data.description,
    ogImage: data.ogImage || 'https://shiftgo.dk/og-image.jpg',
    ogUrl: canonicalUrl,
    ogType: 'website',
    ogSiteName: 'ShiftGo',
    ogLocale: getFullLocale(currentLocale),
    
    // Twitter Cards - Enhanced for maximum engagement
    twitterCard: 'summary_large_image',
    twitterTitle: data.title,
    twitterDescription: data.description,
    twitterImage: data.ogImage || 'https://shiftgo.dk/og-image.jpg',
    twitterSite: '@ShiftGoApp',
    twitterCreator: '@ShiftGoApp',
    twitterImageAlt: data.imageAlt || 'ShiftGo - Professional Shift Management Platform',
    twitterDomain: 'shiftgo.dk',
    
    // Facebook specific
    fbAppId: '1234567890123456', // Replace with actual FB App ID
    
    // LinkedIn specific  
    'article:author': 'ShiftGo Team',
    'article:publisher': 'https://www.linkedin.com/company/shiftgo',
    
    // Additional Open Graph properties
    ogImageWidth: '1200',
    ogImageHeight: '630',
    ogImageAlt: data.imageAlt || 'ShiftGo - Professional Shift Management Platform',
    ogVideoUrl: data.videoUrl,
    ogVideoType: 'video/mp4',
    ogAudioUrl: data.audioUrl,
    ogAudioType: 'audio/mpeg',
    
    // Additional SEO
    robots: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    googlebot: 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1',
    'apple-mobile-web-app-title': 'ShiftGo',
    'application-name': 'ShiftGo',
    'theme-color': '#3b82f6',
    
    // Schema.org JSON-LD will be handled separately
  })
  
  // Set canonical link
  useHead({
    link: [
      { rel: 'canonical', href: canonicalUrl },
      // Hreflang links
      ...Object.entries(alternateUrls).map(([locale, url]) => ({
        rel: 'alternate',
        hreflang: locale === 'dk' ? 'da' : locale,
        href: url
      })),
      // Self-referencing hreflang
      { rel: 'alternate', hreflang: 'x-default', href: alternateUrls.en }
    ]
  })
}

// Helper function for locale mapping
function getFullLocale(locale: string): string {
  const localeMap: Record<string, string> = {
    'tr': 'tr_TR',
    'dk': 'da_DK',
    'en': 'en_US'
  }
  return localeMap[locale] || 'en_US'
}

// SEO-optimized structured data generator - Enhanced with advanced schema types
export const useStructuredData = (type: 'Organization' | 'SoftwareApplication' | 'WebApplication' | 'FAQ' | 'HowTo' | 'Article' | 'BreadcrumbList' | 'Product' | 'Review' | 'LocalBusiness' | 'Service' | 'Course' | 'VideoObject' | 'ImageObject' | 'Person' | 'JobPosting' | 'Event' | 'WebSite', data: any) => {
  const structuredData = generateStructuredData(type, data)
  
  useHead({
    script: [
      {
        type: 'application/ld+json',
        innerHTML: JSON.stringify(structuredData)
      }
    ]
  })
}

function generateStructuredData(type: string, data: any) {
  const baseUrl = 'https://shiftgo.dk'
  
  switch (type) {
    case 'Organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'ShiftGo',
        url: baseUrl,
        logo: `${baseUrl}/logo.png`,
        description: 'Professional shift management and employee scheduling platform',
        foundingDate: '2024',
        sameAs: [
          // Add social media URLs when available
        ],
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer service',
          availableLanguage: ['en', 'tr', 'da']
        }
      }
      
    case 'SoftwareApplication':
      return {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'ShiftGo',
        url: baseUrl,
        description: data.description || 'Professional shift management and employee scheduling platform for modern businesses',
        applicationCategory: 'BusinessApplication',
        applicationSubCategory: 'Human Resources Management',
        operatingSystem: 'Web Browser, iOS, Android, Windows, macOS',
        softwareVersion: data.version || '2.0',
        releaseNotes: data.releaseNotes || 'Enhanced shift management with AI-powered scheduling',
        datePublished: data.datePublished || '2024-01-01',
        author: {
          '@type': 'Organization',
          name: 'ShiftGo',
          url: baseUrl
        },
        offers: [
          {
            '@type': 'Offer',
            name: 'Free Plan',
            price: '0',
            priceCurrency: 'USD',
            eligibleQuantity: {
              '@type': 'QuantitativeValue',
              value: 10,
              unitText: 'employees'
            },
            description: 'Perfect for small teams getting started'
          },
          {
            '@type': 'Offer',
            name: 'Professional Plan',
            price: data.professionalPrice || '9.99',
            priceCurrency: 'USD',
            billingIncrement: 'P1M',
            description: 'Advanced features for growing businesses'
          },
          {
            '@type': 'Offer',
            name: 'Enterprise Plan',
            price: data.enterprisePrice || '19.99',
            priceCurrency: 'USD',
            billingIncrement: 'P1M',
            description: 'Full-featured solution for large organizations'
          }
        ],
        featureList: [
          'AI-Powered Shift Scheduling',
          'Real-time Employee Scheduling',
          'Advanced Time Tracking',
          'Team Collaboration Tools',
          'Multi-language Support (English, Turkish, Danish)',
          'Mobile & Desktop Apps',
          'Advanced Reporting & Analytics',
          'GDPR Compliance',
          'Danish Labor Law Compliance',
          'API Integration',
          'Custom Workflows',
          'Automated Notifications'
        ],
        screenshot: [
          `${baseUrl}/screenshots/dashboard.jpg`,
          `${baseUrl}/screenshots/scheduling.jpg`,
          `${baseUrl}/screenshots/mobile.jpg`
        ],
        video: {
          '@type': 'VideoObject',
          name: 'ShiftGo Demo Video',
          description: 'See how ShiftGo simplifies shift management',
          thumbnailUrl: `${baseUrl}/video-thumbnail.jpg`,
          contentUrl: `${baseUrl}/demo-video.mp4`,
          duration: 'PT2M30S'
        },
        aggregateRating: data.rating ? {
          '@type': 'AggregateRating',
          ratingValue: data.rating.value || '4.8',
          reviewCount: data.rating.count || '150',
          bestRating: '5',
          worstRating: '1'
        } : {
          '@type': 'AggregateRating',
          ratingValue: '4.8',
          reviewCount: '150',
          bestRating: '5',
          worstRating: '1'
        },
        review: data.reviews || [
          {
            '@type': 'Review',
            reviewRating: {
              '@type': 'Rating',
              ratingValue: '5'
            },
            author: {
              '@type': 'Person',
              name: 'Sarah Johnson'
            },
            reviewBody: 'ShiftGo has completely transformed our scheduling process. Highly recommended!'
          }
        ],
        supportingData: {
          '@type': 'Dataset',
          name: 'ShiftGo Performance Metrics',
          description: 'User satisfaction and performance data'
        },
        maintainer: {
          '@type': 'Organization',
          name: 'ShiftGo',
          url: baseUrl
        },
        requirements: 'Modern web browser with JavaScript enabled',
        downloadUrl: data.downloadUrl,
        installUrl: data.installUrl || baseUrl,
        memoryRequirements: '512 MB RAM minimum',
        processorRequirements: 'Any modern processor',
        storageRequirements: '100 MB available space',
        permissions: 'Camera (optional), Notifications, Location (optional)'
      }
      
    case 'WebApplication':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebApplication',
        name: 'ShiftGo',
        url: baseUrl,
        description: data.description || 'Professional shift management and employee scheduling platform',
        applicationCategory: 'BusinessApplication',
        browserRequirements: 'Modern web browser with JavaScript enabled',
        permissions: 'notifications, camera, location'
      }
      
    case 'FAQ':
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: data.questions?.map((q: any) => ({
          '@type': 'Question',
          name: q.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: q.answer
          }
        })) || []
      }

    case 'Article':
      return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: data.headline || data.title,
        description: data.description,
        image: data.image,
        author: data.author || {
          '@type': 'Organization',
          name: 'ShiftGo Team'
        },
        publisher: {
          '@type': 'Organization',
          name: 'ShiftGo',
          logo: {
            '@type': 'ImageObject',
            url: `${baseUrl}/logo.png`
          }
        },
        datePublished: data.datePublished,
        dateModified: data.dateModified || data.datePublished,
        mainEntityOfPage: data.url || baseUrl,
        articleSection: data.section || 'Shift Management',
        wordCount: data.wordCount,
        inLanguage: data.inLanguage || 'en'
      }

    case 'BreadcrumbList':
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: data.items?.map((item: any, index: number) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url
        })) || []
      }

    case 'Product':
      return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: data.name || 'ShiftGo',
        description: data.description || 'Professional shift management platform',
        category: 'Software',
        brand: {
          '@type': 'Brand',
          name: 'ShiftGo'
        },
        offers: {
          '@type': 'Offer',
          url: baseUrl,
          priceCurrency: 'USD',
          price: data.price || '0',
          priceValidUntil: data.priceValidUntil,
          availability: 'https://schema.org/InStock',
          seller: {
            '@type': 'Organization',
            name: 'ShiftGo'
          }
        },
        aggregateRating: data.rating ? {
          '@type': 'AggregateRating',
          ratingValue: data.rating.value,
          reviewCount: data.rating.count,
          bestRating: '5'
        } : undefined
      }

    case 'Review':
      return {
        '@context': 'https://schema.org',
        '@type': 'Review',
        itemReviewed: {
          '@type': 'SoftwareApplication',
          name: 'ShiftGo'
        },
        reviewRating: {
          '@type': 'Rating',
          ratingValue: data.rating,
          bestRating: '5'
        },
        name: data.title,
        author: {
          '@type': 'Person',
          name: data.author
        },
        reviewBody: data.content,
        datePublished: data.date
      }

    case 'LocalBusiness':
      return {
        '@context': 'https://schema.org',
        '@type': 'LocalBusiness',
        name: data.name || 'ShiftGo',
        image: data.image || `${baseUrl}/logo.png`,
        description: data.description,
        address: data.address,
        geo: data.geo,
        url: data.url || baseUrl,
        telephone: data.telephone,
        priceRange: data.priceRange || 'Free - Premium',
        areaServed: data.areaServed,
        serviceArea: data.serviceArea,
        knowsAbout: data.knowsAbout || ['Shift Management', 'Employee Scheduling'],
        aggregateRating: data.rating ? {
          '@type': 'AggregateRating',
          ratingValue: data.rating.value,
          reviewCount: data.rating.count
        } : undefined
      }

    case 'Service':
      return {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: data.name || 'Shift Management Service',
        description: data.description,
        provider: {
          '@type': 'Organization',
          name: 'ShiftGo',
          url: baseUrl
        },
        areaServed: data.areaServed,
        availableChannel: {
          '@type': 'ServiceChannel',
          serviceUrl: data.serviceUrl || baseUrl,
          serviceSmsNumber: data.smsNumber,
          servicePhone: data.phone
        },
        category: data.category || 'Business Software',
        serviceType: data.serviceType || 'Employee Scheduling',
        offers: data.offers ? {
          '@type': 'Offer',
          priceCurrency: data.offers.currency || 'USD',
          price: data.offers.price || '0',
          name: data.offers.name || 'Free Plan'
        } : undefined
      }

    case 'Course':
      return {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: data.name,
        description: data.description,
        provider: {
          '@type': 'Organization',
          name: 'ShiftGo',
          url: baseUrl
        },
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: data.courseMode || 'online',
          instructor: {
            '@type': 'Person',
            name: data.instructor || 'ShiftGo Expert'
          }
        },
        coursePrerequisites: data.prerequisites,
        educationalLevel: data.level || 'Beginner',
        timeRequired: data.duration,
        inLanguage: data.language || 'en'
      }

    case 'VideoObject':
      return {
        '@context': 'https://schema.org',
        '@type': 'VideoObject',
        name: data.name,
        description: data.description,
        thumbnailUrl: data.thumbnailUrl,
        uploadDate: data.uploadDate,
        duration: data.duration,
        contentUrl: data.contentUrl,
        embedUrl: data.embedUrl,
        publisher: {
          '@type': 'Organization',
          name: 'ShiftGo',
          logo: {
            '@type': 'ImageObject',
            url: `${baseUrl}/logo.png`
          }
        },
        potentialAction: {
          '@type': 'SeekToAction',
          target: `${data.contentUrl}?t={seek_to_second_number}`,
          'startOffset-input': 'required name=seek_to_second_number'
        }
      }

    case 'ImageObject':
      return {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        contentUrl: data.contentUrl,
        creditText: data.creditText || 'ShiftGo',
        creator: {
          '@type': 'Organization',
          name: 'ShiftGo'
        },
        copyrightNotice: data.copyrightNotice || '© ShiftGo',
        acquireLicensePage: data.licensePage,
        caption: data.caption,
        representativeOfPage: data.representativeOfPage || true
      }

    case 'Person':
      return {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: data.name,
        jobTitle: data.jobTitle,
        description: data.description,
        image: data.image,
        url: data.url,
        sameAs: data.sameAs || [],
        worksFor: {
          '@type': 'Organization',
          name: 'ShiftGo',
          url: baseUrl
        },
        knowsAbout: data.knowsAbout || ['Shift Management', 'HR', 'Workforce Planning'],
        alumniOf: data.education,
        hasCredential: data.credentials
      }

    case 'JobPosting':
      return {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: data.title,
        description: data.description,
        hiringOrganization: {
          '@type': 'Organization',
          name: data.companyName || 'ShiftGo Partner',
          sameAs: data.companySite
        },
        jobLocation: {
          '@type': 'Place',
          address: data.address
        },
        employmentType: data.employmentType || 'FULL_TIME',
        baseSalary: data.salary ? {
          '@type': 'MonetaryAmount',
          currency: data.salary.currency || 'USD',
          value: {
            '@type': 'QuantitativeValue',
            value: data.salary.value,
            unitText: data.salary.unitText || 'YEAR'
          }
        } : undefined,
        datePosted: data.datePosted,
        validThrough: data.validThrough
      }

    case 'Event':
      return {
        '@context': 'https://schema.org',
        '@type': 'Event',
        name: data.name,
        description: data.description,
        startDate: data.startDate,
        endDate: data.endDate,
        eventStatus: data.status || 'https://schema.org/EventScheduled',
        eventAttendanceMode: data.attendanceMode || 'https://schema.org/OnlineEventAttendanceMode',
        location: data.location ? {
          '@type': 'VirtualLocation',
          url: data.location.url
        } : undefined,
        organizer: {
          '@type': 'Organization',
          name: 'ShiftGo',
          url: baseUrl
        },
        offers: data.offers ? {
          '@type': 'Offer',
          url: data.offers.url,
          price: data.offers.price || '0',
          priceCurrency: data.offers.currency || 'USD',
          availability: 'https://schema.org/InStock'
        } : undefined
      }

    case 'WebSite':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: data.name || 'ShiftGo',
        url: data.url || baseUrl,
        description: data.description || 'Professional shift management and employee scheduling platform',
        inLanguage: ['en', 'tr', 'da'],
        copyrightYear: new Date().getFullYear(),
        copyrightHolder: {
          '@type': 'Organization',
          name: 'ShiftGo'
        },
        potentialAction: data.potentialAction,
        mainEntity: {
          '@type': 'SoftwareApplication',
          name: 'ShiftGo Platform'
        },
        author: {
          '@type': 'Organization',
          name: 'ShiftGo'
        },
        publisher: {
          '@type': 'Organization',
          name: 'ShiftGo',
          logo: {
            '@type': 'ImageObject',
            url: `${baseUrl}/logo.png`
          }
        }
      }
      
    default:
      return {}
  }
}